import i18n from '../i18n';

/**
 * Shared money formatting — the currency sibling of dateUtils' display
 * helpers. All price DISPLAY goes through here so the symbol, its position,
 * and the decimal separator follow the active UI language:
 *   en → "€12.50"   nl → "€ 12,50"
 *
 * Replaces the per-component formatters (symbol-map concatenation and
 * browser-locale Intl calls) that hardcoded English number shapes. Do NOT
 * reintroduce a `CURRENCY_SYMBOLS` map — Intl knows the symbols, and symbol
 * POSITION is locale-dependent, so `sym + value` is wrong in general.
 *
 * Not for price-LEVEL indicators ('$$$' from Google's price_level) — those
 * are symbols, not amounts, and stay as-is.
 */

const displayLocale = () => i18n.language || 'en';

/**
 * Format a monetary amount in the active locale.
 * @param {number} amount - The amount (major units, or cents with fromCents)
 * @param {string} currency - ISO 4217 code; defaults to EUR
 * @param {object} [opts]
 * @param {boolean} [opts.fromCents] - Treat `amount` as cents
 * @param {boolean} [opts.wholeNumbers] - Round to whole units (floor-price
 *   displays like "From €13" where cents are noise)
 * @returns {string|null} Localized money string, null when amount is missing
 */
export const formatCurrency = (amount, currency, { fromCents = false, wholeNumbers = false } = {}) => {
  if (amount == null) return null;
  const value = fromCents ? amount / 100 : amount;
  const code = currency || 'EUR';
  try {
    return new Intl.NumberFormat(displayLocale(), {
      style: 'currency',
      currency: code,
      // Whole-if-integer keeps "€12" clean while "€12.50" keeps its cents.
      maximumFractionDigits: wholeNumbers || value % 1 === 0 ? 0 : 2
    }).format(value);
  } catch (e) {
    // Unknown/garbage currency code from an external feed — degrade to
    // "CODE 12.50" rather than throwing mid-render.
    return `${code} ${value % 1 === 0 ? value : value.toFixed(2)}`;
  }
};

/**
 * Format a price range: "€10–€20" (each endpoint fully formatted — symbol
 * position varies by locale, so a shared leading symbol would be wrong).
 * Collapses to a single price when the endpoints match or one is missing.
 * @returns {string|null} null when both endpoints are missing
 */
export const formatCurrencyRange = (min, max, currency, opts = {}) => {
  // Truthy checks (not null checks) match the formatters this replaces:
  // a 0 endpoint reads as "no price on this side", never renders as "€0".
  if (!min && !max) return null;
  if (min && max && min !== max) {
    return `${formatCurrency(min, currency, opts)}–${formatCurrency(max, currency, opts)}`;
  }
  return formatCurrency(min || max, currency, opts);
};
